/*
 * Surface elevation shadows.
 *
 * These cannot come out of the token generator: they compose `color-mix()` over
 * live custom properties, while the generator only emits static shadow strings.
 * They are the base surface shadows — `classic-overrides.ts` restores the
 * pre-refresh static values when a consumer opts out.
 */

/**
 * Visual refresh surface box shadow assignments — light mode.
 *
 * Light-mode surfaces share one `--_vr-*` composition per elevation level, so
 * every surface/state that maps to the same level renders the same shadow.
 */
export const vrLightBoxShadowAssignments = {
  '--dt-box-shadows-surface-flat-rest': 'var(--_vr-10)',
  '--dt-box-shadows-surface-flat-hover': 'var(--_vr-20)',
  '--dt-box-shadows-surface-flat-active': 'var(--_vr-10)',
  '--dt-box-shadows-surface-flat-drag': 'var(--_vr-50)',
  '--dt-box-shadows-surface-raised-rest': 'var(--_vr-20)',
  '--dt-box-shadows-surface-raised-hover': 'var(--_vr-30)',
  '--dt-box-shadows-surface-raised-active': 'var(--_vr-20)',
  '--dt-box-shadows-surface-raised-drag': 'var(--_vr-50)',
  '--dt-box-shadows-surface-floating-rest': 'var(--_vr-30)',
  '--dt-box-shadows-surface-floating-hover': 'var(--_vr-40)',
  '--dt-box-shadows-surface-floating-active': 'var(--_vr-30)',
  '--dt-box-shadows-surface-floating-drag': 'var(--_vr-50)',
};

/** Visual refresh box shadow elements — light mode. */
export const vrLightBoxShadowElements = {
  '--_vr-shadow-0': '0 0 0 1px var(--dt-colors-border-neutral-subdued)',
  '--_vr-shadow-1':
    '0 1px 2px 0 color-mix(in srgb, var(--dt-colors-box-shadow-default) 5%, transparent)',
  '--_vr-shadow-2-20':
    '0 4px 8px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 7%, transparent)',
  '--_vr-shadow-2-30':
    '0 5px 11px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 16%, transparent)',
  '--_vr-shadow-2-40':
    '0 6px 14px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 24%, transparent)',
  '--_vr-shadow-2-50':
    '0 7px 17px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 32%, transparent)',
};

/** Visual refresh box shadow compositions — light mode. */
export const vrLightBoxShadowCompositions = {
  '--_vr-10': 'var(--_vr-shadow-0)',
  '--_vr-20':
    'var(--_vr-shadow-1), var(--_vr-shadow-2-20), var(--_vr-shadow-0)',
  '--_vr-30':
    'var(--_vr-shadow-1), var(--_vr-shadow-2-30), var(--_vr-shadow-0)',
  '--_vr-40':
    'var(--_vr-shadow-1), var(--_vr-shadow-2-40), var(--_vr-shadow-0)',
  '--_vr-50':
    'var(--_vr-shadow-1), var(--_vr-shadow-2-50), var(--_vr-shadow-0)',
};

/**
 * Visual refresh box shadow elements — dark mode.
 *
 * `--_vr-shadow-0` (BoxShadow01) and `--_vr-shadow-1` (BoxShadow02) are the
 * shared lower shadow layers. The BoxShadow03 layer is not shared in dark mode:
 * its blur differs per interaction state, so it is defined per surface token in
 * `vrDarkBoxShadowAssignments`.
 */
export const vrDarkBoxShadowElements = {
  '--_vr-shadow-0': '0 0 0 1px var(--dt-colors-border-neutral-subdued)',
  '--_vr-shadow-1': '0 1px 2px 0 var(--dt-colors-box-shadow-default)',
};

/** Visual refresh box shadow compositions — dark mode. */
export const vrDarkBoxShadowCompositions = {
  '--_vr-10': 'var(--_vr-shadow-0)',
};

/**
 * Visual refresh surface box shadow assignments — dark mode.
 *
 * Unlike light mode, the dark BoxShadow03 layer uses a per-state blur
 * (rest/active = 12, hover = 14, drag = 17) and a `-2` spread, so each surface
 * token is composed individually instead of sharing a level composition. The
 * `--_vr-shadow-0`/`--_vr-shadow-1` layers and the `-2` spread are unchanged
 * from light; only the dark BoxShadow03 blur and offset vary. Flat rest/active
 * carry no elevation shadow (`--_vr-10`).
 */
export const vrDarkBoxShadowAssignments = {
  '--dt-box-shadows-surface-flat-rest': 'var(--_vr-10)',
  '--dt-box-shadows-surface-flat-hover':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 4px 14px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
  '--dt-box-shadows-surface-flat-active': 'var(--_vr-10)',
  '--dt-box-shadows-surface-flat-drag':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 13px 17px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
  '--dt-box-shadows-surface-raised-rest':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 4px 12px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
  '--dt-box-shadows-surface-raised-hover':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 7px 14px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
  '--dt-box-shadows-surface-raised-active':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 4px 12px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
  '--dt-box-shadows-surface-raised-drag':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 13px 17px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
  '--dt-box-shadows-surface-floating-rest':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 7px 12px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
  '--dt-box-shadows-surface-floating-hover':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 10px 14px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
  '--dt-box-shadows-surface-floating-active':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 7px 12px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
  '--dt-box-shadows-surface-floating-drag':
    'var(--_vr-shadow-0), var(--_vr-shadow-1), 0 13px 17px -2px color-mix(in srgb, var(--dt-colors-box-shadow-default) 80%, transparent)',
};
